import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { UnitCategoryFloorDto } from 'api/models';
import { useTranslation } from 'react-i18next';

const BuildingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 60px;
  height: 100%;
  min-height: 400px;
  perspective: 2000px;
  transform-style: preserve-3d;
`;

const Floor = styled.div<{ $status: 'available' | 'reserved' | 'sold' | 'mixed' | 'empty' }>`
  width: 320px;
  height: 54px;
  margin-bottom: 4px;
  background: ${({ $status }) => {
    switch ($status) {
      case 'available':
        return 'linear-gradient(45deg, #52c41a, #95de64)';
      case 'reserved':
        return 'linear-gradient(45deg, #faad14, #ffd666)';
      case 'sold':
        return 'linear-gradient(45deg, #ff4d4f, #ff7875)';
      case 'mixed':
        return 'linear-gradient(45deg, #1890ff, #69c0ff)';
      case 'empty':
        return 'linear-gradient(45deg, #d9d9d9, #f0f0f0)';
      default:
        return 'linear-gradient(45deg, #d9d9d9, #f0f0f0)';
    }
  }};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  transform: rotateX(15deg) rotateY(-25deg) translateZ(0);
  transform-style: preserve-3d;
  transform-origin: center center;
  box-shadow: 8px 8px 24px rgba(0, 0, 0, 0.15);

  &:not(:first-child) {
    --offset: 0;
  }

  &:hover {
    transform: rotateX(15deg) rotateY(-25deg) translateZ(20px);
    box-shadow: 12px 12px 32px rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: '';
    position: absolute;
    width: 100px;
    height: 100%;
    right: -100px;
    top: 0;
    background: inherit;
    filter: brightness(0.7);
    transform: rotateY(90deg);
    transform-origin: left;
    border-radius: 0 4px 4px 0;
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100px;
    left: 0;
    bottom: -100px;
    background: inherit;
    filter: brightness(0.6);
    transform: rotateX(-90deg);
    transform-origin: top;
    border-radius: 0 0 4px 4px;
  }
`;

const FloorLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  height: 100%;
  color: white;
  font-weight: 500;
  font-size: 14px;
  transform: translateZ(20px);
`;

const FloorDescription = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
`;

const FloorStats = styled.div`  
  font-size: 12px;
  display: flex;
  gap: 12px;
  white-space: nowrap;
`;

interface Props {
  floors: UnitCategoryFloorDto[];
  onFloorClick: (floor: UnitCategoryFloorDto) => void;
}

export const BuildingVisualization: React.FC<Props> = ({ floors, onFloorClick }) => {
  const { t } = useTranslation();

  const getFloorStatus = (floor: UnitCategoryFloorDto) => {
    if (floor.units && floor.units.length > 0) {
      const totalStock = floor.units.reduce((sum, unit) => sum + (unit.stock || 0), 0) || 0;
      const totalSold = floor.units.reduce((sum, unit) => sum + (unit.sold || 0), 0) || 0;
      const totalReserved = floor.units.reduce((sum, unit) => sum + (unit.reserved || 0), 0) || 0;
      const totalAvailable = totalStock - (totalSold + totalReserved);

      if (totalStock === 0) return 'empty';
      if (totalSold === totalStock) return 'sold';
      if (totalAvailable === totalStock) return 'available';
      if (totalReserved > 0) return 'reserved';
      return 'mixed';
    }

    const totalUnits = floor.unitsCount || 0;
    const soldUnits = floor.soldUnitsCount || 0;
    const reservedUnits = floor.reservedUnitsCount || 0;
    const availableUnits = floor.availableUnitsCount || 0;

    if (totalUnits === 0) return 'empty';
    if (soldUnits === totalUnits) return 'sold';
    if (availableUnits === totalUnits) return 'available';
    if (reservedUnits > 0) return 'reserved';
    return 'mixed';
  };

  const getTooltipContent = (floor: UnitCategoryFloorDto) => (
    <div style={{ padding: '8px' }}>
      <div style={{ fontWeight: 500, marginBottom: '4px' }}>
        {floor.description || t('categories:floor-number', 'Floor {{number}}', { number: floor.floorNumber })}
      </div>
      <div>
        {t('categories:total-units', 'Total Units')}: {floor.unitsCount}
      </div>
      <div>
        {t('categories:available-units', 'Available')}: {floor.availableUnitsCount}
      </div>
      <div>
        {t('categories:reserved-units', 'Reserved')}: {floor.reservedUnitsCount}
      </div>
      <div>
        {t('categories:sold-units', 'Sold')}: {floor.soldUnitsCount}
      </div>
    </div>
  );

  const sortedFloors = useMemo(() => {
    return [...floors].sort((a, b) => (b.floorNumber || 0) - (a.floorNumber || 0));
  }, [floors]);

  return (
    <BuildingWrapper>
      {sortedFloors.map((floor, index) => (
        <Tooltip key={floor.id} title={getTooltipContent(floor)} placement="left">
          <Floor
            $status={getFloorStatus(floor)}
            onClick={() => onFloorClick(floor)}
            style={{ '--index': index } as React.CSSProperties}
          >
            <FloorLabel>
              <div>
                <div>
                  {floor.description || t('categories:floor-number', 'Floor {{number}}', { number: floor.floorNumber })}
                </div>
              </div>
              <FloorStats>
                <span>
                  {t('categories:available-short', 'A')}: {floor.availableUnitsCount}
                </span>
                <span>
                  {t('categories:reserved-short', 'R')}: {floor.reservedUnitsCount}
                </span>
                <span>
                  {t('categories:sold-short', 'S')}: {floor.soldUnitsCount}
                </span>
              </FloorStats>
            </FloorLabel>
          </Floor>
        </Tooltip>
      ))}
    </BuildingWrapper>
  );
};